/*
  "You save" total — Sableau cart page and Essential drawer
  ------------------------------------------------------------
  The shopper was only ever shown one part of what he saves. The checkout prints
  a native TOTAL SAVINGS row, but that row counts the discount code and the
  automatic bundle tier only: the compare-at reduction that is already baked into
  every price is invisible to it, and so is the shipping fee we waive over the
  threshold. Three separate savings, never added up anywhere.

  This adds one block, in the two places we control, that states all three and
  their sum:

      Sale discount     the compare-at price minus the price actually charged
      Extra discount    cart.total_discount (bundle tier, WELCOME10, ...)
      Shipping          the fee waived, only once the cart really qualifies
      You save          the three added together

  Currency
  --------
  Every figure is derived live from amounts that are already in the visitor's
  own currency, and formatted from a price string taken off the page, so the
  symbol, the separators and the decimals match whatever the market renders.
  Nothing is precomputed and frozen: a stored money string is exactly how the
  checkout "You save" property ended up showing euro next to a krone total
  (2026-08-20), and this file must not repeat that.

  The compare-at figure is the one number the cart API does not carry, so it is
  taken from /products/{handle}.js as a RATIO (compare_at / price) and applied to
  the line total the cart already reports. A ratio has no currency, so it stays
  correct in every market no matter what that endpoint returns.

  If any part is missing or looks wrong, the block renders nothing rather than
  guessing. A missing row costs a little persuasion; a wrong one costs trust at
  the last screen before payment.
*/
(function () {
  'use strict';

  if (window.__sbTotalSavingsLoaded) return;
  window.__sbTotalSavingsLoaded = true;

  var CONFIG = {};
  try {
    var node = document.getElementById('sb-savings-config');
    if (node) CONFIG = JSON.parse(node.textContent || '{}');
  } catch (e) { /* guards below stop the script */ }

  var MARK = 'sb-total-savings';
  var DRAWER_FOOTER = '[data-essential-cart-element="cart-drawer-footer"]';
  var DRAWER_CHECKOUT = '[data-essential-cart-element="checkout-button-wrapper"]';
  var DRAWER_SUBTOTAL = '[data-essential-cart-element="subtotal-wrapper"]';
  var PAGE_TOTAL = '.cart-totals__total';

  // Currencies whose everyday amounts carry no decimals (same list local-money.js uses).
  var WHOLE_UNIT = ['NOK', 'SEK', 'DKK', 'ISK', 'JPY', 'HUF', 'CZK', 'KRW', 'CLP'];
  var PRICE_ONLY = /^\s*(?:[^\d\s]{0,4}\s?)?\d[\d.,\s ]*(?:\s?[^\d\s]{0,4})?\s*$/;

  var STYLE =
    '.' + MARK + '{margin:.6rem 0;padding:.55rem .7rem;border-radius:6px;' +
    'background:#f2f9f4;border:1px solid #d7e9dc;font-size:.8rem;line-height:1.5}' +
    '.' + MARK + '__row{display:flex;justify-content:space-between;gap:1rem;color:#3d4a41}' +
    '.' + MARK + '__row--total{margin-top:.3rem;padding-top:.3rem;' +
    'border-top:1px solid #cfe2d5;color:#0a7a42;font-weight:700}';

  function currencyCode() {
    try { return (window.Shopify && Shopify.currency && Shopify.currency.active) || CONFIG.shopCurrency || 'EUR'; }
    catch (e) { return 'EUR'; }
  }
  function rate() {
    try { return parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || '1') || 1; }
    catch (e) { return 1; }
  }

  /* A price container repeats its label and amount ("Subtotal: €9,99Subtotal€9,99"),
     so walk down to the leaf that holds nothing but a number. */
  function priceLeaf(root) {
    if (!root) return null;
    var nodes = root.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].children.length) continue;
      var t = (nodes[i].textContent || '').replace(/ /g, ' ').trim();
      if (t && t.length <= 24 && /\d/.test(t) && PRICE_ONLY.test(t)) return t;
    }
    var own = (root.textContent || '').replace(/ /g, ' ').trim();
    return PRICE_ONLY.test(own) ? own : null;
  }

  /* "€99,99" · "kr 1.113,07" · "1 158,00 kr" · "£85.00" -> the pieces needed to
     render another amount in exactly the same shape. */
  function parseAmount(str) {
    var txt = String(str).replace(/ /g, ' ');
    var m = txt.match(/(\d[\d.,\s]*\d|\d)/);
    if (!m) return null;
    var numStr = m[1];
    var dec = numStr.match(/([.,])(\d{2})\s*$/);
    var intPart = (dec ? numStr.slice(0, dec.index) : numStr).replace(/[.,\s]/g, '');
    if (!intPart) return null;
    var rawPrefix = txt.slice(0, txt.indexOf(numStr));
    var rawSuffix = txt.slice(txt.indexOf(numStr) + numStr.length);
    return {
      decSep: dec ? dec[1] : ',',
      thouSep: (numStr.match(/\d([.,\s])\d{3}/) || [])[1] || '',
      prefix: rawPrefix.trim(),
      suffix: rawSuffix.trim(),
      /* Keep the market's own spacing: "€134,92" has none, "kr 1.113,07" does.
         Re-adding a space unconditionally made our row read "€ 150,00" beside
         the theme's "€134,92". */
      prefixGap: /\s$/.test(rawPrefix) ? ' ' : '',
      suffixGap: /^\s/.test(rawSuffix) ? ' ' : ''
    };
  }

  function format(sample, cents) {
    var code = currencyCode();
    var whole = Math.floor(cents / 100);
    var frac = String(cents % 100);
    if (frac.length < 2) frac = '0' + frac;
    var ws = String(whole);
    if (sample && sample.thouSep) ws = ws.replace(/\B(?=(\d{3})+(?!\d))/g, sample.thouSep);
    if (!sample) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en',
          { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }).format(cents / 100);
      } catch (e) { return (cents / 100).toFixed(2) + ' ' + code; }
    }
    var body = (WHOLE_UNIT.indexOf(code) !== -1 && cents % 100 === 0) ? ws : ws + sample.decSep + frac;
    return (sample.prefix ? sample.prefix + sample.prefixGap : '') + body +
           (sample.suffix ? sample.suffixGap + sample.suffix : '');
  }

  /* The threshold is configured in shop currency. Convert, then round up, which
     is the rule local-money.js already prints on the product page — rounding up
     can only ask for slightly more than Shopify really requires, so nobody is
     told he qualifies when he does not. */
  function thresholdCents() {
    var base = parseFloat(CONFIG.threshold);
    if (isNaN(base) || base <= 0) return 0;
    var code = currencyCode();
    if (code === (CONFIG.shopCurrency || 'EUR')) return Math.round(base * 100);
    var value = base * rate();
    var step = value >= 100 ? 50 : 5;
    return Math.ceil(value / step) * step * 100;
  }

  function shippingSavedCents() {
    var fee = parseFloat(CONFIG.fee);
    if (isNaN(fee) || fee <= 0) return 0;
    var code = currencyCode();
    if (code === (CONFIG.shopCurrency || 'EUR')) return Math.round(fee * 100);
    var value = fee * rate();
    return WHOLE_UNIT.indexOf(code) !== -1 ? Math.ceil(value) * 100 : Math.ceil(value * 100);
  }

  function shipsFree() {
    var list = String(CONFIG.countries || '').toUpperCase().replace(/\s/g, '').split(',');
    var here = String(CONFIG.country || '').toUpperCase();
    return here !== '' && list.indexOf(here) !== -1;
  }

  // ---- compare-at, as a currency-free ratio -------------------------------
  var productCache = {};
  function productData(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  function saleSavedCents(cart) {
    var jobs = cart.items.map(function (item) {
      if (!item.handle || !item.original_line_price) return Promise.resolve(0);
      return productData(item.handle).then(function (p) {
        if (!p || !p.variants) return 0;
        var v = null;
        for (var i = 0; i < p.variants.length; i++) {
          if (p.variants[i].id === item.variant_id) { v = p.variants[i]; break; }
        }
        if (!v || !v.price || !v.compare_at_price) return 0;
        var ratio = v.compare_at_price / v.price;
        if (!(ratio > 1)) return 0;
        return Math.round(item.original_line_price * ratio) - item.original_line_price;
      }).catch(function () { return 0; });
    });
    return Promise.all(jobs).then(function (all) {
      return all.reduce(function (a, b) { return a + b; }, 0);
    });
  }

  function row(label, value, cls) {
    var el = document.createElement('div');
    el.className = MARK + '__row' + (cls ? ' ' + MARK + '__row--' + cls : '');
    var a = document.createElement('span'); a.textContent = label;
    var b = document.createElement('span'); b.textContent = value;
    el.appendChild(a); el.appendChild(b);
    return el;
  }

  function build(sample, sale, extra, ship) {
    var box = document.createElement('div');
    box.className = MARK;
    if (sale > 0) box.appendChild(row('Sale discount', format(sample, sale)));
    if (extra > 0) box.appendChild(row('Extra discount', format(sample, extra)));
    if (ship > 0) box.appendChild(row('Shipping', format(sample, ship)));
    box.appendChild(row('You save', format(sample, sale + extra + ship), 'total'));
    return box;
  }

  function place(box, signature) {
    var targets = [];
    var footer = document.querySelector(DRAWER_FOOTER) || document.querySelector(DRAWER_CHECKOUT);
    if (footer) targets.push({ parent: footer, before: document.querySelector(DRAWER_CHECKOUT) });
    var pageTotal = document.querySelector(PAGE_TOTAL);
    if (pageTotal && pageTotal.parentElement) targets.push({ parent: pageTotal.parentElement, before: pageTotal });

    targets.forEach(function (t) {
      if (!t.parent) return;
      var existing = t.parent.querySelector(':scope > .' + MARK);
      if (existing && existing.getAttribute('data-sb-signature') === signature) return;
      var clone = box.cloneNode(true);
      clone.setAttribute('data-sb-signature', signature);
      if (existing) t.parent.replaceChild(clone, existing);
      else if (t.before && t.before.parentElement === t.parent) t.parent.insertBefore(clone, t.before);
      else t.parent.appendChild(clone);
    });
  }

  function clear() {
    var all = document.querySelectorAll('.' + MARK);
    for (var i = 0; i < all.length; i++) all[i].parentElement.removeChild(all[i]);
    appSavingsRow(false);
  }

  /* The Essential drawer prints its own "Savings -X" line, which counts the
     compare-at reduction and the extra discount but not the waived shipping.
     Left alone it sits directly above our block, so the shopper reads two
     different savings totals one click before paying. Ours is the same figure
     plus shipping, itemised, so the app's line is hidden while ours is up --
     and put back the moment ours is not, so a failure never leaves the drawer
     with no savings shown at all. */
  function appSavingsRow(hide) {
    var label = document.querySelector('[data-essential-cart-element="discount-code-savings-label"]');
    if (!label) return;
    var row = label.closest('[data-essential-cart-element="discount-code-row"]');
    if (!row) return;
    if (hide) {
      if (row.style.display !== 'none') row.style.display = 'none';
    } else if (row.style.display === 'none') {
      row.style.display = '';
    }
  }

  var running = false;
  function apply() {
    if (running) return;
    if (!document.querySelector(DRAWER_FOOTER) && !document.querySelector(DRAWER_CHECKOUT)
        && !document.querySelector(PAGE_TOTAL)) return;
    running = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.item_count) { clear(); return; }
        return saleSavedCents(cart).then(function (sale) {
          var extra = cart.total_discount || 0;
          var qualifies = shipsFree() && thresholdCents() > 0 && cart.total_price >= thresholdCents();
          var ship = qualifies ? shippingSavedCents() : 0;
          if (sale + extra + ship <= 0) { clear(); return; }

          var sampleTxt = priceLeaf(document.querySelector(DRAWER_SUBTOTAL))
            || priceLeaf(document.querySelector(PAGE_TOTAL));
          var sample = sampleTxt ? parseAmount(sampleTxt) : null;
          var signature = [currencyCode(), sale, extra, ship, cart.total_price].join('|');
          place(build(sample, sale, extra, ship), signature);
          appSavingsRow(document.querySelector('.' + MARK) !== null);
        });
      })
      .catch(function () { /* leave the page as it is */ })
      .then(function () { running = false; });
  }

  function start() {
    var style = document.createElement('style');
    style.textContent = STYLE;
    (document.head || document.documentElement).appendChild(style);

    var queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      setTimeout(function () { queued = false; try { apply(); } catch (e) {} }, 250);
    }
    try {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes && muts[i].addedNodes.length) { schedule(); return; }
        }
      }).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    document.addEventListener('cart:updated', schedule);
    document.addEventListener('cart:refresh', schedule);
    var n = 0;
    var timer = setInterval(function () { schedule(); if (++n > 20) clearInterval(timer); }, 1000);
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
